import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Select } from '../../components/UI';
import { usersAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { HiOutlinePlus, HiOutlineTrash, HiOutlineExclamationCircle, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';

const roleOptions = [
  { value: 'telecaller', label: 'Telecaller' },
  // { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
  // { value: 'super_admin', label: 'Super Admin' },
];

const phoneLabelOptions = [
  { value: 'primary', label: 'Primary' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'home', label: 'Home' },
  { value: 'work', label: 'Work' },
  { value: 'other', label: 'Other' },
];

// Validate Indian phone number (10 digits, optionally with +91 prefix)
const validatePhoneNumber = (phone) => {
  if (!phone) return { isValid: false, error: 'Phone number is required' };

  // Remove spaces and hyphens
  let cleanPhone = phone.toString().replace(/[\s\-]/g, '');

  // Remove +91 prefix if present
  if (cleanPhone.startsWith('+91')) {
    cleanPhone = cleanPhone.substring(3);
  } else if (cleanPhone.startsWith('91') && cleanPhone.length === 12) {
    // Handle case where 91 is prefixed without +
    cleanPhone = cleanPhone.substring(2);
  }

  // Check if it's exactly 10 digits
  if (!/^\d{10}$/.test(cleanPhone)) {
    return { isValid: false, error: 'Phone must be 10 digits' };
  }

  // Check if it starts with 6-9
  if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
    return { isValid: false, error: 'Phone must start with 6-9' };
  }

  return { isValid: true, cleanPhone };
};

// Validate email
const validateEmail = (email) => {
  if (!email) return { isValid: false, error: 'Email is required' };
  const cleanEmail = email.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
    return { isValid: false, error: 'Invalid email format' };
  }
  return { isValid: true, cleanEmail };
};

// Validate name
const validateName = (name, fieldName = 'Name') => {
  if (!name) return { isValid: false, error: `${fieldName} is required` };
  const cleanName = name.trim();
  if (cleanName.length < 2) return { isValid: false, error: `${fieldName} must be at least 2 characters` };
  if (/\d/.test(cleanName)) return { isValid: false, error: `${fieldName} cannot contain numbers` };
  if (!/^[a-zA-Z\s]+$/.test(cleanName)) return { isValid: false, error: `${fieldName} can only contain letters and spaces` };
  return { isValid: true, cleanName };
};

const UserModal = ({ isOpen, onClose, user, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phones: [{ number: '', label: 'primary', isDefault: true }],
    password: '',
    role: 'telecaller',
    // department: '',
    // designation: '',
    isActive: true,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isEditing = !!user;

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phones: user.phones?.length > 0 ? user.phones : [{ number: '', label: 'primary', isDefault: true }],
        password: '', // Always start with empty password when editing
        role: user.role || 'telecaller',
        // department: user.department || '',
        // designation: user.designation || '',
        isActive: user.isActive !== false, // Handle undefined/null as true
      });
    } else {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phones: [{ number: '', label: 'primary', isDefault: true }],
        password: '',
        role: 'telecaller',
        // department: '',
        // designation: '',
        isActive: true,
      });
    }
    setErrors({});
  }, [user, isOpen]);

  const validate = () => {
    const newErrors = {};

    // Validate first name
    const firstNameValidation = validateName(formData.firstName, 'First name');
    if (!firstNameValidation.isValid) {
      newErrors.firstName = firstNameValidation.error;
    }

    // Validate last name
    const lastNameValidation = validateName(formData.lastName, 'Last name');
    if (!lastNameValidation.isValid) {
      newErrors.lastName = lastNameValidation.error;
    }

    // Validate email
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error;
    }

    // Validate password (required for new users)
    if (!isEditing && !formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    // Validate phone numbers
    const validPhones = formData.phones.filter(p => p.number && p.number.trim());
    if (validPhones.length === 0) {
      newErrors.phones = 'At least one phone number is required';
    } else {
      const phoneErrors = [];
      for (const phone of validPhones) {
        const validation = validatePhoneNumber(phone.number);
        if (!validation.isValid) {
          phoneErrors.push(validation.error);
        }
      }
      if (phoneErrors.length > 0) {
        newErrors.phones = phoneErrors[0]; // Show first error
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Prevent numbers in name fields
    if ((name === 'firstName' || name === 'lastName') && /\d/.test(value)) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (index, field, value) => {
    // For phone number, allow digits, +, spaces, and hyphens
    if (field === 'number') {
      // Allow empty value or valid phone characters
      if (value && !/^[\d\s\-+]*$/.test(value)) {
        return;
      }
      // Limit to reasonable length (including +91 prefix)
      if (value && value.replace(/[\s\-]/g, '').length > 15) {
        return;
      }
    }

    setFormData(prev => {
      const newPhones = [...prev.phones];
      newPhones[index] = { ...newPhones[index], [field]: value };

      // If setting a phone as default, remove default from others
      if (field === 'isDefault' && value === true) {
        newPhones.forEach((phone, i) => {
          if (i !== index) {
            newPhones[i] = { ...phone, isDefault: false };
          }
        });
        // Also set label to primary for default
        newPhones[index].label = 'primary';
      }

      return { ...prev, phones: newPhones };
    });
    if (errors.phones) {
      setErrors(prev => ({ ...prev, phones: '' }));
    }
  };

  const addPhone = () => {
    setFormData(prev => ({
      ...prev,
      phones: [...prev.phones, { number: '', label: 'secondary', isDefault: false }],
    }));
  };

  const removePhone = (index) => {
    if (formData.phones.length === 1) {
      toast.error('At least one phone number is required');
      return;
    }
    setFormData(prev => ({
      ...prev,
      phones: prev.phones.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const data = { ...formData };

      // Clean and format phone numbers
      const cleanedPhones = data.phones
        .filter(p => p.number && p.number.trim())
        .map(p => {
          // Validate and clean phone number
          const validation = validatePhoneNumber(p.number);
          return {
            ...p,
            number: validation.isValid ? validation.cleanPhone : p.number.replace(/[\s\-]/g, ''),
          };
        });

      if (cleanedPhones.length === 0) {
        setErrors({ phones: 'At least one phone number is required' });
        setIsSubmitting(false);
        return;
      }

      // Ensure at least one phone is default
      if (!cleanedPhones.some(p => p.isDefault)) {
        cleanedPhones[0].isDefault = true;
        cleanedPhones[0].label = 'primary';
      }

      data.phones = cleanedPhones;

      // Clean email to lowercase
      data.email = data.email.trim().toLowerCase();

      // Don't send empty password for editing
      if (isEditing && !data.password) {
        delete data.password;
      }

      // Ensure isActive is properly set as boolean
      data.isActive = data.isActive === true || data.isActive === 'true';

      if (isEditing) {
        await usersAPI.update(user._id, data);
        toast.success('User updated successfully');
      } else {
        await usersAPI.create(data);
        toast.success('User created successfully');
      }
      onSuccess();
      onClose();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to save user';
      if (error.response?.data?.errors) {
        // Handle validation errors from backend
        const backendErrors = error.response.data.errors;
        setErrors(prev => ({ ...prev, ...backendErrors }));
      } else {
        toast.error(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit User' : 'Add New User'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name *"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="Enter first name"
            error={errors.firstName}
            onInput={(e) => e.target.value = e.target.value.replace(/\d/g, '')}
          />
          <Input
            label="Last Name *"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Enter last name"
            error={errors.lastName}
            onInput={(e) => e.target.value = e.target.value.replace(/\d/g, '')}
          />
        </div>

        <div>
          <Input
            label="Email *"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter email address"
            error={errors.email}
            disabled={isEditing}
          />
          {isEditing && (
            <p className="text-xs text-gray-500 mt-1">
              Email cannot be changed after creation
            </p>
          )}
        </div>

        {/* Phone Numbers Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Phone Numbers *
              </label>
              <p className="text-xs text-gray-500 mt-0.5">
                Enter 10-digit Indian mobile number (can include +91 prefix)
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              icon={<HiOutlinePlus className="w-4 h-4" />}
              onClick={addPhone}
            >
              Add Phone
            </Button>
          </div>

          {errors.phones && (
            <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
              <HiOutlineExclamationCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600">{errors.phones}</p>
            </div>
          )}

          <div className="space-y-3">
            {formData.phones.map((phone, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1 grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Input
                      placeholder="e.g., 9876543210 or +91 9876543210"
                      value={phone.number}
                      onChange={(e) => handlePhoneChange(index, 'number', e.target.value)}
                    />
                  </div>
                  <Select
                    options={phoneLabelOptions}
                    value={phone.label}
                    onChange={(e) => handlePhoneChange(index, 'label', e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <label className="flex items-center gap-1 text-xs text-gray-600">
                    <input
                      type="radio"
                      name="defaultPhone"
                      checked={phone.isDefault}
                      onChange={() => handlePhoneChange(index, 'isDefault', true)}
                      className="w-3 h-3 text-primary-600"
                    />
                    Default
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                    onClick={() => removePhone(index)}
                    disabled={formData.phones.length === 1}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Input
            label={isEditing ? 'New Password' : 'Password *'}
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            placeholder={isEditing ? 'Leave blank to keep current password' : 'Enter password (min 6 characters)'}
            error={errors.password}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="text-secondary-400 hover:text-secondary-600 focus:outline-none"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword
                  ? <HiOutlineEyeOff className="w-5 h-5" />
                  : <HiOutlineEye className="w-5 h-5" />}
              </button>
            }
          />
          {isEditing && (
            <p className="text-xs text-gray-500 mt-1">
              Leave blank to keep the current password unchanged
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Role *"
            name="role"
            options={roleOptions}
            value={formData.role}
            onChange={handleChange}
          />
          {/* <Input
            label="Department"
            name="department"
            value={formData.department}
            onChange={handleChange}
            placeholder="e.g., Sales"
          /> */}
        </div>

        {/* <Input
          label="Designation"
          name="designation"
          value={formData.designation}
          onChange={handleChange}
          placeholder="e.g., Senior Telecaller"
        /> */}

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            name="isActive"
            id="isActive"
            checked={formData.isActive}
            onChange={handleChange}
            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="isActive" className="text-sm text-gray-700">
            Active User
          </label>
        </div>

        <div className="flex gap-3 justify-end pt-4 border-t border-gray-100">
          <Button variant="ghost" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={isSubmitting}>
            {isEditing ? 'Update User' : 'Create User'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UserModal;